import { useEffect, useState } from "react";
import ModalComponent from "../../../../components/modals/ModalFormComponent";
import Label from "../../../../components/form_fields/Label";
import { SubmitHandler, useForm } from "react-hook-form";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import {
	useCreateBenefit,
	useUpdateBenefit,
} from "../../../../services/benefit.service";
import {
	enumToStringConverter,
	FREQUENCY,
	IAddEditTableForm,
	manageResourceIds,
} from "../../../../helpers/appHelpers";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import FormDescriptionInput from "../../../../components/form_fields/FormDescriptionInput";
import { BenefitType } from "../../../../models/benefit-type.dto";
import MoneyInput from "../../../../components/form_fields/MoneyInput";
import { useManageTableWithSearchParamsData } from "../../../../helpers/useManageTable";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import { TaxCodeDto } from "../../../../models/tax-code.dto";
import { EmployeeBandDto } from "../../../../models/employee-band.dto";
import { EmployeeDto } from "../../../../models/employee.dto";
import FormCodeInput from "../../../../components/form_fields/FormCodeInput";
import MultiSelectField from "../../../../components/form_fields/MultiSelectField";
import { PATHS } from "../../../../routes/routes.paths";
import { ExplicitDeductionDto } from "../../../../models/explicit-deduction.dto";
import { PayPeriodDto } from "../../../../models/pay-period.dto";
import APISelectField from "../../../../components/form_fields/APISelectField";
import { BenefitGroupDto } from "../../../../models/benefit-group.dto";

interface IFormInputs {
	benefitTypeInstanceId: number;
	name: string;
	code: string;
	description: string;
	value: number;
	taxCode?: string;
	frequency: string;
	groupId?: number;
	employeeIds?: number[];
	employeeBandIds?: number[];
	deductionIds?: number[];
	payPeriodIds?: number[];
}

type IBenefitModalForm = IAddEditTableForm & {
	benefitTypes: BenefitType[];
	taxCodes: TaxCodeDto[];
	employees: EmployeeDto[];
	employeeBands: EmployeeBandDto[];
	explicitDeductions: ExplicitDeductionDto[];
	payPeriods: PayPeriodDto[];
	benefitGroups: BenefitGroupDto[];
	reloadTableData?: () => void;
};

const BenefitModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	benefitTypes,
	taxCodes,
	employees,
	employeeBands,
	explicitDeductions,
	payPeriods,
	benefitGroups,
	reloadTableData,
}: IBenefitModalForm) => {
	const { currentCompany } = useCompanyStore();
	const { formDataValues } = useEditFormStore();
	const isGrouped = isEditForm && !!formDataValues?.groupId;
	const [uploading, setUploading] = useState(false);
	const [benefitType, setBenefitType] = useState("");

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			name: isEditForm ? formDataValues?.name : "",
			code: isEditForm ? formDataValues?.code : "",
			description: isEditForm ? formDataValues?.description : "",
			benefitTypeInstanceId: isEditForm
				? formDataValues?.benefitTypeInstanceId
				: undefined,
			value: isEditForm ? formDataValues?.value : undefined,
			taxCode: isEditForm ? formDataValues?.taxCode : "",
			frequency: isEditForm ? formDataValues?.frequency : "",
			groupId: isEditForm ? formDataValues?.groupId : undefined,
		},
	});

	const taxCode = watch("taxCode");
	useEffect(() => {
		if (taxCode && benefitType === "IN_KIND") {
			setValue("value", undefined as any);
		}
	}, [taxCode]);

	const benefitTypeInstanceId = watch("benefitTypeInstanceId");
	useEffect(() => {
		if (!benefitTypeInstanceId) {
			setBenefitType("");
			return;
		}

		const selected = benefitTypes.find(
			(benefitType) => benefitType.id === benefitTypeInstanceId,
		);

		setBenefitType(selected?.benefitType || "");

		switch (selected?.benefitType) {
			case "GIFT":
				setValue("value", undefined as any);
				setValue("frequency", undefined as any);
				setValue("taxCode", undefined as any);
				break;
			case "IN_KIND":
				if (taxCode) {
					setValue("value", undefined as any);
				}
				break;
			case "MEDICAL":
				setValue("frequency", undefined as any);
				break;
		}
	}, [benefitTypeInstanceId]);

	const [employeeIds, setEmployeeIds] = useState<number[]>([]);
	const [employeeBandIds, setEmployeeBandIds] = useState<number[]>([]);
	const [deductionIds, setDeductionIds] = useState<number[]>([]);
	const [payPeriodIds, setPayPeriodIds] = useState<number[]>([]);
	const { mutate: createBenefit } = useCreateBenefit();
	const { mutate: updateBenefit } = useUpdateBenefit();
	const { addNewTableData } = useManageTableWithSearchParamsData();

	const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
		setUploading(true);

		if (isEditForm) {
			const { addIds: addDeductionIds, removeIds: removeDeductionIds } =
				manageResourceIds({
					newData: deductionIds,
					oldData:
						formDataValues?.explicitDeductions?.map(
							(deduction) => deduction.id,
						) || [],
				});
			const { addIds: addPayPeriodIds, removeIds: removePayPeriodIds } =
				manageResourceIds({
					newData: payPeriodIds,
					oldData:
						formDataValues?.payPeriods?.map((payPeriod) => payPeriod.id) || [],
				});
			const { addIds: addEmployeeIds, removeIds: removeEmployeeIds } =
				manageResourceIds({
					newData: employeeIds,
					oldData:
						formDataValues?.employeeBenefits?.map(
							(employeeBenefit) => employeeBenefit?.employee.id,
						) || [],
				});
			const { addIds: addEmployeeBandIds, removeIds: removeEmployeeBandIds } =
				manageResourceIds({
					newData: employeeBandIds,
					oldData:
						formDataValues?.employeeBandBenefits?.map(
							(employeeBandBenefit) => employeeBandBenefit?.employeeBand.id,
						) || [],
				});

			data.addDeductionIds = addDeductionIds;
			data.removeDeductionIds = removeDeductionIds;
			data.addPayPeriodIds = addPayPeriodIds;
			data.removePayPeriodIds = removePayPeriodIds;
			data.addEmployeeIds = addEmployeeIds;
			data.removeEmployeeIds = removeEmployeeIds;
			data.addEmployeeBandIds = addEmployeeBandIds;
			data.removeEmployeeBandIds = removeEmployeeBandIds;

			updateBenefit(
				{ payload: data, id: formDataValues?.id },
				{
					onSuccess: (updatedData) => {
						try {
							tableAPIRef?.current.updateRows([updatedData.data]);
						} catch (error) {
							console.error("Error updating table rows:", error);
							reloadTableData?.();
						}
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			data.deductionIds = deductionIds.length
				? (deductionIds as any)
				: undefined;
			data.payPeriodIds = payPeriodIds.length
				? (payPeriodIds as any)
				: undefined;
			data.employeeIds = employeeIds.length ? (employeeIds as any) : undefined;
			data.employeeBandIds = employeeBandIds.length
				? (employeeBandIds as any)
				: undefined;

			createBenefit(
				{ payload: { companyId: currentCompany?.id, ...data } },
				{
					onSuccess: () => {
						addNewTableData();
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	const handleClose = () => {
		reset();
		setOpen(false);
	};

	return (
		<ModalComponent
			width={775}
			open={open}
			title={title}
			handleClose={handleClose}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-6">
					{isGrouped && (
						<p className="col-span-2 text-sm text-amber-600 bg-amber-50 rounded-lg px-4 py-2">
							Some fields are managed by the benefit group and cannot be edited
							here.
						</p>
					)}
					<div className="w-full">
						<Label title="Name" for="name" />
						<input
							autoFocus
							placeholder={"Enter name"}
							type="text"
							{...register("name", {
								required: "Enter name here",
							})}
							id="name"
							disabled={isGrouped}
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
								isGrouped ? "opacity-50 cursor-not-allowed" : ""
							} ${
								errors.name
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.name && (
							<p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Unique Code" for="code" />
						<FormCodeInput
							register={register}
							attributes={{
								disabled: isGrouped,
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									isGrouped ? "opacity-50 cursor-not-allowed" : ""
								} ${
									errors.code
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
						/>
						{errors.code && (
							<p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
						)}
					</div>
					<div className="col-span-2 w-full">
						<Label title="Description" for="description" optional />
						<FormDescriptionInput
							register={register}
							attributes={{
								defaultValue: isEditForm ? formDataValues?.description : "",
								disabled: isGrouped,
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									isGrouped ? "opacity-50 cursor-not-allowed" : ""
								} ${
									errors.description
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
							required={false}
						/>
						{errors.description && (
							<p className="text-red-500 text-sm mt-1">
								{errors.description.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Benefit Type" for="benefitTypeInstanceId" />
						<select
							disabled={isGrouped}
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
								isGrouped ? "opacity-50 cursor-not-allowed" : ""
							} ${
								errors.benefitTypeInstanceId
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
							{...register("benefitTypeInstanceId", {
								required: isGrouped ? false : "Select benefit type here",
								valueAsNumber: true,
							})}
						>
							<option value="">Select Type</option>
							{benefitTypes.map((benefitType) => (
								<option key={benefitType.id} value={benefitType.id}>
									{benefitType.name}
								</option>
							))}
						</select>
						{errors.benefitTypeInstanceId && (
							<p className="text-red-500 text-sm mt-1">
								{errors.benefitTypeInstanceId.message}
							</p>
						)}
					</div>
					{isEditForm && (
						<div className="w-full">
							<Label title="Benefit Group" for="groupId" optional />
							<APISelectField
								key="groupId"
								attributes={{
									id: "groupId",
									defaultValue: isEditForm
										? formDataValues?.groupId
										: undefined,
									disabled: isGrouped,
								}}
								placeholder="Select Benefit Group"
								register={register}
								name="groupId"
								options={benefitGroups}
								pathname={PATHS.SUB_COMPANY_SETTINGS.BENEFIT_GROUPS}
								required={false}
								error={errors.groupId}
								valueAsNumber
							/>
						</div>
					)}

					{benefitType !== "GIFT" && (
						<>
							{benefitType === "IN_KIND" && !taxCode && (
								<div className="w-full">
									<Label title="Value" for="value" />
									<MoneyInput
										attributes={{
											id: "value",
											placeholder: "0",
										}}
										register={register}
										name="value"
										setValue={setValue}
										defaultValue={
											isEditForm ? formDataValues?.value : undefined
										}
										required={"Enter value here"}
										error={errors.value}
									/>
									{errors.value && (
										<p className="text-red-500 text-sm mt-1">
											{errors.value.message}
										</p>
									)}
								</div>
							)}
							{(benefitType === "MEDICAL" || !benefitType) && (
								<div className="w-full">
									<Label title="Value" for="value" optional />
									<MoneyInput
										attributes={{
											id: "value",
											placeholder: "0",
										}}
										register={register}
										name="value"
										setValue={setValue}
										defaultValue={
											isEditForm ? formDataValues?.value : undefined
										}
										required={false}
										error={errors.value}
									/>
									{errors.value && (
										<p className="text-red-500 text-sm mt-1">
											{errors.value.message}
										</p>
									)}
								</div>
							)}
							<div className="w-full">
								<Label title="Tax Code" for="taxCode" optional />
								<select
									disabled={isGrouped}
									className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
										isGrouped ? "opacity-50 cursor-not-allowed" : ""
									} ${
										errors.taxCode
											? " border border-red-500 focus:border-red-500 focus:outline-red-500"
											: "border-none"
									}`}
									{...register("taxCode", {
										required: false,
									})}
								>
									<option value="">Select Tax Code</option>
									{taxCodes.map((taxCode) => (
										<option key={taxCode.code} value={taxCode.code}>
											{taxCode.code}
										</option>
									))}
								</select>
								{errors.taxCode && (
									<p className="text-red-500 text-sm mt-1">
										{errors.taxCode.message}
									</p>
								)}
							</div>
							{benefitType !== "MEDICAL" && (
								<div className="w-full">
									<Label title="Frequency" for="frequency" />
									<select
										disabled={isGrouped}
										className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm mb-2 ${
											isGrouped ? "opacity-50 cursor-not-allowed" : ""
										} ${
											errors.frequency
												? " border border-red-500 focus:border-red-500 focus:outline-red-500"
												: "border-none"
										}`}
										{...register("frequency", {
											required: isGrouped ? false : "Select frequency here",
										})}
									>
										<option value="">Select frequency</option>
										{FREQUENCY.map((item, index) => (
											<option key={index} value={item}>
												{enumToStringConverter(item)}
											</option>
										))}
									</select>
									{errors.frequency && (
										<p className="text-red-500 text-sm mt-1">
											{errors.frequency.message}
										</p>
									)}
								</div>
							)}
						</>
					)}
					<div className="w-full">
						<Label title="Assign Employees" for="employeeIds" optional />
						<MultiSelectField
							key={"employeeIds"}
							options={employees}
							setResourceIds={setEmployeeIds}
							placeholder={"Select Employees"}
							pathname={PATHS.EMPLOYEES}
							required={false}
							mapTexts={["firstName", "lastName"]}
							disabled={isGrouped}
							defaultValues={
								!isEditForm
									? []
									: formDataValues?.employeeBenefits?.map(
											(employeeBenefit) => employeeBenefit?.employee.id,
										) || []
							}
						/>
					</div>
					<div className="w-full">
						<Label
							title="Assign Employee Bands"
							for="employeeBandIds"
							optional
						/>
						<MultiSelectField
							key={"employeeBandIds"}
							options={employeeBands}
							setResourceIds={setEmployeeBandIds}
							placeholder={"Select Bands"}
							pathname={PATHS.SUB_COMPANY_SETTINGS.BANDS}
							required={false}
							defaultValues={
								!isEditForm
									? []
									: formDataValues?.employeeBandBenefits?.map(
											(employeeBandBenefit) =>
												employeeBandBenefit?.employeeBand.id,
										) || []
							}
						/>
					</div>
					<div className="w-full">
						<Label title="Assign Pay Periods" for="payPeriodIds" optional />
						<MultiSelectField
							key={"payPeriodIds"}
							options={payPeriods}
							setResourceIds={setPayPeriodIds}
							placeholder={"Select Pay Periods"}
							pathname={PATHS.SUB_COMPANY_SETTINGS.PAY_PERIODS}
							mapText={"code"}
							required={false}
							defaultValues={
								!isEditForm
									? []
									: formDataValues?.payPeriods?.map(
											(payPeriod) => payPeriod.id,
										) || []
							}
						/>
					</div>
					<div className="w-full">
						<Label
							title="Assign Explicit Deductions"
							for="deductionIds"
							optional
						/>
						<MultiSelectField
							key={"deductionIds"}
							options={explicitDeductions}
							setResourceIds={setDeductionIds}
							placeholder={"Select Deductions"}
							pathname={PATHS.DEDUCTIONS_AND_LOANS + "?view=explicitDeductions"}
							required={false}
							defaultValues={
								!isEditForm
									? []
									: formDataValues?.explicitDeductions?.map(
											(deduction) => deduction.id,
										) || []
							}
						/>
					</div>
				</div>
				<div className="w-full flex justify-end mt-12">
					<OrangeButton
						type="submit"
						title={`${
							uploading
								? "Uploading..."
								: isEditForm
									? "Update Benefit"
									: "Add Benefit"
						}`}
						className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
						disabled={uploading}
					/>
				</div>
			</form>
		</ModalComponent>
	);
};

export default BenefitModalForm;
